import { inject, Injectable } from '@angular/core';

import { V1AuthUser } from '@app/models/api/v1/auth/auth.model';
import { SPECIFIC_EVENT_NAME, SPECIFIC_WORKSPACE_ID } from '@app/services/integrations/specific/specific.tokens';
import { ScriptLoaderService } from '@app/services/script-loader.service';

declare global {
  interface Window {
    Specific: {
      appId: string;
      _queue: unknown[];
      (...args: unknown[]): void;
    };
  }
}

type SpecificUser = Partial<{
  uuid: string;
  language: string;
  company: Partial<{ uuid: string }>;
}>;

@Injectable({
  providedIn: 'root',
})
export class SpecificService {
  private readonly workspaceId = inject(SPECIFIC_WORKSPACE_ID);
  private readonly eventName = inject(SPECIFIC_EVENT_NAME);
  private readonly scriptLoaderService = inject(ScriptLoaderService);
  private user: V1AuthUser | undefined;
  private lastUser: SpecificUser | undefined;
  private isInitialized = false;

  public initSpecificUser(user: V1AuthUser): void {
    this.user = user;
  }

  public logoutSpecificUser(): void {
    this.user = undefined;
    this.lastUser = undefined;
    Specific('logoutUser');
  }

  public async showFeedback(): Promise<void> {
    try {
      await this.loadSpecific();
      this.setSpecificUser();
      Specific('event', this.eventName, { loadingPlacement: 'center', autoFocus: true, showInitialLoading: true });
    } catch {
      //
    }
  }

  public async trackEvent(event: string, properties?: Record<string, unknown>): Promise<void> {
    try {
      await this.loadSpecific();
      this.setSpecificUser();
      Specific('event', event, properties);
    } catch {
      //
    }
  }

  private loadSpecific(): Promise<void> {
    if (!this.isInitialized) {
      const _queue: unknown[] = [];
      window.Specific = Object.assign(
        (...args: unknown[]): void => {
          _queue.push(args);
        },
        { appId: this.workspaceId, _queue },
      );
      this.isInitialized = true;
    }

    return this.scriptLoaderService.load('https://cdn.specific.app/bundle.js', { queryParams: { id: this.workspaceId } });
  }

  private setSpecificUser(): void {
    if (this.lastUser?.uuid !== this.user?.uuid) {
      Specific('setUserId', this.user?.uuid);
    }

    if (this.lastUser?.language !== this.user?.language) {
      Specific('setLanguage', this.user?.language);
    }

    if (this.user?.company?.uuid && this.lastUser?.company?.uuid !== this.user?.company?.uuid) {
      Specific('setGroup', this.user?.company?.uuid, {});
    }

    this.lastUser = {
      uuid: this.user?.uuid,
      language: this.user?.language,
      company: {
        uuid: this.user?.company?.uuid,
      },
    };
  }
}
